import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex } from "theme-ui";
import moment from "moment-timezone";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import MergeLocationsModal from "components/MergeLocationsModal";
import {
  findDuplicateLocations,
  archiveLocation,
  deleteLocation,
  getLocationReferenceCounts,
  DuplicateLocationCluster,
  DuplicateLocationMember,
} from "api/Locations";

/**
 * FindDuplicateLocations \u2014 Session 24
 *
 * Directory of near-duplicate Location clusters, powered by GET
 * /locations/duplicates. Backend groups by:
 *   - same_address: normalized (LOWER + TRIM) line1 collisions
 *   - same_coords:  3-decimal bounding box + haversine within ~25m
 *
 * Each cluster is rendered as a card. The card auto-picks a "suggested
 * survivor" (highest downstream reference count) and offers per-non-
 * survivor actions:
 *
 *   \u2192 Merge into survivor: opens MergeLocationsModal (dry-run \u2192 confirm)
 *   \u2192 Archive: soft-delete via the Session 23 endpoint
 *   \u2192 Delete: hard-delete via the Session 23 endpoint. Only enabled
 *              when this Location has zero downstream references.
 *
 * The suggested survivor is a hint \u2014 the rep can promote any member
 * to survivor via the "Set as survivor" button before merging.
 */

const formatAddress = (addr: any): string => {
  if (!addr) return "";
  const parts = [addr.line1, addr.city, addr.state].filter(Boolean);
  return parts.join(", ");
};

const styles: Record<string, React.CSSProperties> = {
  intro: {
    fontSize: 13,
    color: "#5D5E62",
    marginBottom: 16,
    lineHeight: 1.5,
  },
  clusterCard: {
    border: "1px solid #E4E7EB",
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    background: "#FFFFFF",
  },
  clusterHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
    gap: 12,
  },
  clusterTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#111",
    margin: 0,
  },
  clusterMeta: {
    fontSize: 12,
    color: "#5D5E62",
    marginTop: 2,
  },
  reasonBadge: {
    display: "inline-block",
    padding: "2px 8px",
    background: "#F3F4F6",
    color: "#374151",
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  memberRow: {
    display: "flex",
    alignItems: "center",
    padding: "10px 0",
    borderTop: "1px solid #F3F4F6",
    gap: 12,
  },
  memberMain: {
    flex: 1,
    minWidth: 0,
  },
  memberName: {
    fontSize: 13,
    fontWeight: 500,
    color: "#111",
  },
  memberAddress: {
    fontSize: 12,
    color: "#5D5E62",
    marginTop: 2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  memberStats: {
    fontSize: 11,
    color: "#78716C",
    marginTop: 4,
  },
  survivorBadge: {
    display: "inline-block",
    padding: "2px 6px",
    background: "#DCFCE7",
    color: "#166534",
    borderRadius: 3,
    fontSize: 10,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginLeft: 6,
  },
  memberActions: {
    display: "flex",
    gap: 6,
    flexShrink: 0,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  actionButton: {
    fontSize: 11,
    padding: "4px 10px",
    background: "#FFFFFF",
    border: "1px solid #E4E7EB",
    borderRadius: 4,
    cursor: "pointer",
    color: "#111",
  },
  actionButtonPrimary: {
    fontSize: 11,
    padding: "4px 10px",
    background: "#111",
    border: "1px solid #111",
    borderRadius: 4,
    cursor: "pointer",
    color: "#FFF",
  },
  actionButtonDanger: {
    fontSize: 11,
    padding: "4px 10px",
    background: "#FFFFFF",
    border: "1px solid #FCA5A5",
    borderRadius: 4,
    cursor: "pointer",
    color: "#991B1B",
  },
  emptyState: {
    padding: "40px 20px",
    textAlign: "center",
    color: "#5D5E62",
    fontSize: 13,
  },
  errorBanner: {
    marginBottom: 12,
    padding: "10px 14px",
    background: "#FEE2E2",
    border: "1px solid #FCA5A5",
    borderRadius: 6,
    fontSize: 13,
    color: "#991B1B",
  },
};

const scoreMember = (m: DuplicateLocationMember): number =>
  m.recordCount * 10 + m.mapCount * 3 + m.profileCount;

const FindDuplicateLocations: React.FC = () => {
  const navigate = useNavigate();
  const [clusters, setClusters] = useState<DuplicateLocationCluster[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Per-cluster survivor selection. Keyed by cluster.key, holds the
  // Location id the rep picked as survivor. Null / missing = auto-pick
  // (highest score wins).
  const [survivorOverrides, setSurvivorOverrides] = useState<
    Record<string, string>
  >({});
  // Which merge is currently open. Holds both source + target so the
  // modal knows what to dry-run.
  const [mergeTarget, setMergeTarget] = useState<{
    source: DuplicateLocationMember;
    target: DuplicateLocationMember;
  } | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    const res = await findDuplicateLocations();
    setClusters(res);
    setLoading(false);
  }, []);

  useEffect(() => {
    document.title = `${process.env.REACT_APP_NAME} | Duplicate locations`;
    refetch();
  }, [refetch]);

  const totalDupeLocations = useMemo(
    () => clusters.reduce((sum, c) => sum + c.locations.length, 0),
    [clusters],
  );

  const resolveSurvivor = (
    cluster: DuplicateLocationCluster,
  ): DuplicateLocationMember => {
    const overrideId = survivorOverrides[cluster.key];
    if (overrideId) {
      const picked = cluster.locations.find((m) => m.id === overrideId);
      if (picked) return picked;
    }
    // Auto: highest score, tiebreak on oldest createdAt (the OG row is
    // usually the one you want to keep).
    return [...cluster.locations].sort((a, b) => {
      const s = scoreMember(b) - scoreMember(a);
      if (s !== 0) return s;
      return (
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
    })[0];
  };

  const handleArchive = async (m: DuplicateLocationMember) => {
    const ok = window.confirm(
      `Archive "${m.name || "(unnamed)"}"? It will disappear from the directory. Records at this Location keep working. Archive is reversible from the Location detail page.`,
    );
    if (!ok) return;
    setBusyId(m.id);
    try {
      const res: any = await archiveLocation(m.id);
      if ((res as any)?.error) {
        setError((res as any).error);
        return;
      }
      await refetch();
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (m: DuplicateLocationMember) => {
    setBusyId(m.id);
    try {
      // Pre-check so the confirm dialog is honest about what will happen.
      // Backend enforces refs=0 regardless, but a rejected DELETE with
      // 409 is a worse UX than a client-side pre-flight.
      const refs = await getLocationReferenceCounts(m.id);
      if (refs && refs.total > 0) {
        window.alert(
          `Cannot delete "${m.name || "(unnamed)"}" \u2014 ${refs.total} downstream reference(s). Merge into another Location or archive instead.`,
        );
        return;
      }
      const ok = window.confirm(
        `Delete "${m.name || "(unnamed)"}" permanently? This can't be undone.`,
      );
      if (!ok) return;
      const res: any = await deleteLocation(m.id);
      if ((res as any)?.error) {
        setError((res as any).error);
        return;
      }
      await refetch();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <Header />
      <Page
        title="Duplicate locations"
        subtitle="Clusters of near-duplicate sites, grouped by matching address or coordinates."
      >
        <Flex sx={{ mb: 16, gap: 8, justifyContent: "flex-end" }}>
          <BasicButton
            type="button"
            scheme="secondary"
            outline
            onClick={() => navigate("/locations")}
            styles={{ margin: "0" }}
            keepText
          >
            Back to all locations
          </BasicButton>
          <BasicButton
            type="button"
            scheme="secondary"
            outline
            onClick={refetch}
            disabled={loading}
            styles={{ margin: "0" }}
            keepText
          >
            {loading ? "Refreshing\u2026" : "Refresh"}
          </BasicButton>
        </Flex>

        {error && <Box style={styles.errorBanner}>{error}</Box>}

        <p style={styles.intro}>
          {loading
            ? "Scanning for duplicates\u2026"
            : clusters.length === 0
              ? "No duplicate clusters detected. All Locations look distinct by address and coordinates."
              : `${clusters.length} cluster${clusters.length === 1 ? "" : "s"} spanning ${totalDupeLocations} Location${totalDupeLocations === 1 ? "" : "s"}. Each cluster suggests a survivor (highest reference count). Merge non-survivors into the survivor, or archive / delete individually.`}
        </p>

        {clusters.map((cluster) => {
          const survivor = resolveSurvivor(cluster);
          return (
            <div key={cluster.key} style={styles.clusterCard}>
              <div style={styles.clusterHeader}>
                <div>
                  <div style={styles.clusterTitle}>
                    {cluster.locations.length} location
                    {cluster.locations.length === 1 ? "" : "s"} matching
                  </div>
                  <div style={styles.clusterMeta}>
                    Cluster key: <code>{cluster.key}</code>
                  </div>
                </div>
                <span style={styles.reasonBadge}>
                  {cluster.reason === "same_address"
                    ? "Same address"
                    : "Same coords (~25m)"}
                </span>
              </div>

              {cluster.locations.map((m) => {
                const isSurvivor = m.id === survivor.id;
                const hasRefs =
                  m.recordCount > 0 || m.mapCount > 0 || m.profileCount > 0;
                return (
                  <div key={m.id} style={styles.memberRow}>
                    <div style={styles.memberMain}>
                      <div style={styles.memberName}>
                        <button
                          type="button"
                          onClick={() => navigate(`/locations/${m.id}`)}
                          style={{
                            background: "transparent",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                            font: "inherit",
                            color: "inherit",
                            fontWeight: 500,
                            textAlign: "left",
                          }}
                          title="Open Location detail"
                        >
                          {m.name || "(unnamed)"}
                        </button>
                        {isSurvivor && (
                          <span style={styles.survivorBadge}>Survivor</span>
                        )}
                      </div>
                      <div style={styles.memberAddress}>
                        {formatAddress(m.address) || "\u2014"}
                        {m.latitude != null && m.longitude != null && (
                          <>
                            {" \u00b7 "}
                            {m.latitude.toFixed(5)}, {m.longitude.toFixed(5)}
                          </>
                        )}
                      </div>
                      <div style={styles.memberStats}>
                        {m.recordCount} record{m.recordCount === 1 ? "" : "s"}
                        {" \u00b7 "}
                        {m.mapCount} map{m.mapCount === 1 ? "" : "s"}
                        {" \u00b7 "}
                        {m.profileCount} profile
                        {m.profileCount === 1 ? "" : "s"}
                        {" \u00b7 "}
                        created {moment(m.createdAt).format("MMM D, YYYY")}
                      </div>
                    </div>

                    <div style={styles.memberActions}>
                      {isSurvivor ? (
                        <span
                          style={{
                            fontSize: 11,
                            color: "#78716C",
                            fontStyle: "italic",
                            padding: "4px 10px",
                          }}
                        >
                          Merge others into this one
                        </span>
                      ) : (
                        <>
                          <button
                            type="button"
                            style={styles.actionButton}
                            onClick={() =>
                              setSurvivorOverrides((prev) => ({
                                ...prev,
                                [cluster.key]: m.id,
                              }))
                            }
                            title="Promote this Location to survivor for this cluster"
                          >
                            Set as survivor
                          </button>
                          <button
                            type="button"
                            style={styles.actionButtonPrimary}
                            onClick={() =>
                              setMergeTarget({ source: m, target: survivor })
                            }
                            disabled={busyId === m.id}
                            title={`Merge this Location into ${survivor.name || "(unnamed)"}`}
                          >
                            Merge into survivor
                          </button>
                          <button
                            type="button"
                            style={styles.actionButton}
                            onClick={() => handleArchive(m)}
                            disabled={busyId === m.id}
                          >
                            Archive
                          </button>
                          <button
                            type="button"
                            style={styles.actionButtonDanger}
                            onClick={() => handleDelete(m)}
                            disabled={busyId === m.id || hasRefs}
                            title={
                              hasRefs
                                ? "Cannot delete \u2014 has downstream references. Merge or archive instead."
                                : "Delete permanently"
                            }
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </Page>

      {mergeTarget && (
        <MergeLocationsModal
          source={mergeTarget.source}
          target={mergeTarget.target}
          onCancel={() => setMergeTarget(null)}
          onDone={async () => {
            setMergeTarget(null);
            await refetch();
          }}
        />
      )}
    </>
  );
};

export default FindDuplicateLocations;
